import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, ChevronUp, ChevronDown, ChevronsUpDown, X } from "lucide-react";
import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import "./Produtos.css";
import { ProductApi, ProductType, ProductTypeLabels } from "@/utils/models/Product";
import { useGlobalModal } from "@/components/GlobalModalProvider/GlobalModalProvider";
import { useInvalidateCache } from "@/utils/hooks/useInvalidateCache";
import { showApiErrorModal } from "@/utils/common/apiErrorHelper";

const PRODUCTS_PATH = "/product"; // ajuste se necessário

interface ProductFilters {
  name: string;
  type: ProductType | '';
  isRecurring: 'true' | 'false' | '';
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

async function fetchProductsRequest(
  token: string | undefined, 
  page = 1, 
  pageSize = 20,
  filters?: ProductFilters
) {
  return api.get(PRODUCTS_PATH, {
    params: { 
      page, 
      pageSize,
      name: filters?.name || undefined,
      type: filters?.type || undefined,
      isRecurring: filters?.isRecurring || undefined,
      sortBy: filters?.sortBy || undefined,
      sortOrder: filters?.sortOrder || undefined,
    },
    headers: { Authorization: `Bearer ${token}` },
  });
}

async function createProductRequest(token: string | undefined, payload: Partial<ProductApi>) {
  return api.post(PRODUCTS_PATH, payload, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
}

async function updateProductRequest(token: string | undefined, id: string, payload: Partial<ProductApi>) {
  return api.patch(`${PRODUCTS_PATH}/${id}`, payload, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
}

async function deleteProductRequest(token: string | undefined, id: string) {
  return api.delete(`${PRODUCTS_PATH}/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

function normalizePriceValue(price: string) {
  return Number(String(price).replace(",", "."));
}

/* ---------- ConfirmModal (novo) ---------- */
function ConfirmModal({
  open,
  title = "Confirmação",
  message,
  confirmLabel = "Remover",
  cancelLabel = "Cancelar",
  loading = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title?: string;
  message?: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;

  return (
    <div className="pp-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
      <div className="pp-modal pp-modal-confirm" role="document">
        <h3 id="confirm-title" className="pp-modal-title">{title}</h3>
        <div className="pp-modal-body">
          {message}
        </div>
        <div className="pp-modal-actions">
          <button className="pp-btn pp-btn-ghost" onClick={onCancel} disabled={loading}>{cancelLabel}</button>
          <button className="pp-btn pp-btn-danger" onClick={onConfirm} disabled={loading}>
            {loading ? 'Removendo...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
/* ----------------------------------------- */

function Modal({ open, onClose, children }: { open: boolean; onClose: () => void; children?: React.ReactNode }) {
  if (!open) return null;
  return (
    <div className="pp-modal-overlay" role="dialog" aria-modal="true">
      <div className="pp-modal">
        <button className="pp-modal-close" onClick={onClose} aria-label="Fechar">✕</button>
        {children}
      </div>
    </div>
  );
}

function ProductForm({ initial, onCancel, onSave, submitting }: { initial?: Partial<ProductApi>; onCancel: () => void; onSave: (payload: { name: string; description?: string; price: string; type: ProductType; isRecurring: boolean; recurringInterval: string }) => void; submitting?: boolean; }) {
  const [name, setName] = useState(initial?.name ?? "");
  const [desc, setDesc] = useState(initial?.description ?? "");
  const [price, setPrice] = useState(initial?.price ?? "");
  const [type, setType] = useState<ProductType>(initial?.type ?? "OUTRO");
  const [recurring, setRecurring] = useState(false)
  const [recurringInterval, setrecurringInterval] = useState<'monthly' | 'quarterly' | 'semiannual' | 'annual'>("monthly");

  const modal = useGlobalModal();

  useEffect(() => {
    setName(initial?.name ?? "");
    setDesc(initial?.description ?? "");
    setPrice(initial?.price ?? "");
    setType(initial?.type ?? "OUTRO");
    setRecurring(Boolean(initial?.isRecurring ??  false));
    setrecurringInterval((initial?.recurringInterval as any) ?? "monthly");
  }, [initial]);

  const handleRecurrenceValue = (v:boolean) => {
    setRecurring(v);
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(/[^\d,.]/g, '');
    value = value.replace(/[.,]/g, (match) => match === ',' ? ',' : '.');
    const parts = value.split(/[.,]/);
    if (parts[1] && parts[1].length > 2) {
      value = parts[0] + ',' + parts[1].substring(0, 2);
    }
    setPrice(value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name?.trim()) {
      const successMessage = "Preencha o nome do produto!";
      if (modal) {
        modal.show({ type: "warning", title: "Campos vazios", message: successMessage, autoClose: 3000 });
      } else {
        window.alert(successMessage);
      }
      return;
    }

    if (!price?.trim()) {
      const successMessage = "Preencha o preço do produto!";
      if (modal) {
        modal.show({ type: "warning", title: "Campos vazios", message: successMessage, autoClose: 3000 });
      } else {
        window.alert(successMessage);
      }
      return;
    }
    onSave({ name: name.trim(), description: desc.trim(), price: price.trim(), type, isRecurring: recurring, recurringInterval: recurringInterval });
  };

  return (
    <form className="pp-form" onSubmit={(e) => { handleSubmit(e); }}>
      <label className="pp-field">
        <span className="pp-label">Nome</span>
        <input className="pp-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Fornecimento Endereço Fiscal"  />
      </label>

      <label className="pp-field">
        <span className="pp-label">Descrição</span>
        <textarea className="pp-textarea" rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Pequena descrição" />
      </label>

      <label className="pp-field">
        <span className="pp-label">Preço</span>
        <input className="pp-input" value={price} onChange={handlePriceChange} placeholder="1000,00" />
      </label>

      <div className="form-group">
        <label className="form-group-label">Tipo do Produto</label>
        <select value={type} onChange={e => setType(e.target.value as ProductType)} className="form-control">
          <option value="OUTRO">Outro</option>
          <option value="SERVICO">Serviço</option>
          <option value="ESPACO_COMODIDADE">Espaço/Comodidade</option>
        </select>
      </div>

      <div className="form-group">
        <label className="form-group-label">Essa produto tem assinatura recorrente?</label>
        <div className="radio-group">
          <label className="radio-option">
            <input type="radio" name="recorrente" value="no" checked={recurring=== false} onChange={() => handleRecurrenceValue(false)} /> Não
          </label>
          <label className="radio-option">
            <input type="radio" name="recorrente" value="yes" checked={recurring === true} onChange={() => handleRecurrenceValue(true)} /> Sim
          </label>
        </div>
      </div>

      {recurring && (
        <div className="form-group">
          <label className="form-group-label">Frequência de envio</label>
          <select value={recurringInterval} onChange={e => setrecurringInterval(e.target.value as any)} className="form-control">
            <option value="monthly">Mensal</option>
            <option value="quarterly">Trimestral</option>
            <option value="semiannual">Semestral</option>
            <option value="annual">Anual</option>
          </select>
        </div>
      )}

      <div className="pp-actions">
        <button type="button" className="pp-btn pp-btn-ghost" onClick={onCancel}>Cancelar</button>
        <button type="submit" className="pp-btn pp-btn-primary" disabled={submitting}>{submitting ? 'Salvando...' : 'Salvar'}</button>
      </div>
    </form>
  );
}

// Fetch function for React Query
async function fetchProducts(token: string, page: number, pageSize: number, filters?: ProductFilters) {
  const resp = await fetchProductsRequest(token, page, pageSize, filters);
  const raw = (resp && (resp as any).data) ? (resp as any).data : resp;
  let list: ProductApi[] = [];
  if (Array.isArray(raw)) list = raw as ProductApi[];
  else if (Array.isArray(raw.items)) list = raw.items as ProductApi[];
  else if (Array.isArray(raw.data)) list = raw.data as ProductApi[];
  else if (Array.isArray((raw as any).products)) list = (raw as any).products as ProductApi[];
  const tot = raw?.total ?? raw?.count ?? raw?.totalItems ?? null;
  return { products: list, total: typeof tot === 'number' ? tot : null };
}

// Sort icon component
function SortIcon({ field, currentSort, currentOrder }: { field: string; currentSort: string; currentOrder: 'asc' | 'desc' }) {
  if (currentSort !== field) {
    return <ChevronsUpDown size={14} className="pp-sort-icon pp-sort-inactive" />;
  }
  return currentOrder === 'asc' 
    ? <ChevronUp size={14} className="pp-sort-icon pp-sort-active" />
    : <ChevronDown size={14} className="pp-sort-icon pp-sort-active" />;
}

export default function Produtos() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<ProductApi | null>(null);
  const [deleting, setDeleting] = useState(false) // usado como loading da exclusão
  const [saving, setSaving] = useState(false);

  // novos estados para confirm modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deletingName, setDeletingName] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<ProductType | ''>('');
  const [recurringFilter, setRecurringFilter] = useState<'true' | 'false' | ''>('');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showFilters, setShowFilters] = useState(false);

  const userContext = useUser();
  const token = userContext?.userData?.auth;

  const modal = useGlobalModal();
  const { invalidateProducts } = useInvalidateCache();
  const queryClient = useQueryClient();

  // Debounce search term
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  // Build filters object
  const filters: ProductFilters = {
    name: debouncedSearch,
    type: typeFilter,
    isRecurring: recurringFilter,
    sortBy,
    sortOrder,
  };

  // Use React Query for data fetching with caching
  const { data, isLoading: loading, refetch } = useQuery({
    queryKey: ['products', page, pageSize, debouncedSearch, typeFilter, recurringFilter, sortBy, sortOrder],
    queryFn: () => fetchProducts(token!, page, pageSize, filters),
    enabled: !!token,
  });

  const products = data?.products ?? [];
  const total = data?.total ?? null;

  // Handle column sort
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setTypeFilter('');
    setRecurringFilter('');
    setSortBy('name');
    setSortOrder('asc');
    setPage(1);
  };

  const hasActiveFilters = searchTerm || typeFilter || recurringFilter;

  const handleCreate = async (payload: { name: string; description?: string; price: string; type: ProductType; isRecurring: boolean; recurringInterval: string }) => {
    // valida simples no parent:
    if (!payload.name || payload.name.trim().length < 2) {
      modal?.show({ type: "error", title: "Dados inválidos", message: "Nome do produto inválido.", autoClose: 3000 });
      return;
    }
    const numeric = normalizePriceValue(payload.price);
    if (!payload.price || Number.isNaN(numeric) || numeric <= 0) {
      modal?.show({ type: "error", title: "Dados inválidos", message: "Informe um preço numérico maior que 0.", autoClose: 3000 });
      return;
    }

    setSaving(true);
    try {
      await createProductRequest(token, {
        ...payload,
        name: payload.name.trim(),
        description: payload.description?.trim() || undefined,
        price: String(numeric),
      });
      setCreateOpen(false);
      setPage(1);
      modal?.show({ type: "success", title: "Sucesso", message: "Produto criado.", autoClose: 2500 });
      await invalidateProducts();
      await queryClient.invalidateQueries({ queryKey: ['products'] });
    } catch (err:any) {
      console.error('Erro ao criar produto:', err);
      console.error('Server response:', err.response?.data);
      showApiErrorModal(modal, err, {
        title: "Erro ao criar produto",
        fallbackMessage: "Erro ao criar produto",
        autoClose: 3000,
      });
    } finally { setSaving(false); }
  };


  const handleEdit = async (id: string, payload: { name: string; description?: string; price: string; type: ProductType; isRecurring: boolean; recurringInterval: string }) => {
    if (!payload.name || payload.name.trim().length < 2) {
      modal?.show({ type: "error", title: "Dados inválidos", message: "Nome do produto inválido.", autoClose: 3000 });
      return;
    }
    const numeric = normalizePriceValue(payload.price);
    if (!payload.price || Number.isNaN(numeric) || numeric <= 0) {
      modal?.show({ type: "error", title: "Dados inválidos", message: "Informe um preço numérico maior que 0.", autoClose: 3000 });
      return;
    }

    setSaving(true);
    try {
      await updateProductRequest(token, id, {
        ...payload,
        name: payload.name.trim(),
        description: payload.description?.trim() || undefined,
        price: String(numeric),
      });
      const successMessage = "Produto Atualizado com sucesso!";
      if (modal) {
        modal.show({ type: "success", title: "Sucesso", message: successMessage, autoClose: 3000 });
      } else {
        window.alert(successMessage);
      }
      setEditOpen(false);
      setEditing(null);
      await invalidateProducts();
      await queryClient.invalidateQueries({ queryKey: ['products'] });
    } catch (err) {
      console.error('Erro ao atualizar produto:', err);
      const message = showApiErrorModal(modal, err, {
        title: "Erro ao atualizar produto",
        fallbackMessage: "Erro ao atualizar produto",
        autoClose: 3000,
      });
      if (!modal) {
        window.alert(message);
      }
    } finally { setSaving(false); }
  };

  // função antiga removida. Usamos modal de confirmação agora.
  // const handleDelete = async (id: string) => { ... }

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setDeletingId(null);
    setDeletingName(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingId) return;
    setDeleting(true);
    try {
      await deleteProductRequest(token, deletingId);
      setConfirmOpen(false);
      setDeletingId(null);
      setDeletingName(null);
      const successMessage = "Produto deletado com sucesso!";
      if (modal) {
        modal.show({ type: "success", title: "Sucesso", message: successMessage, autoClose: 3000 });
      } else {
        window.alert(successMessage);
      }
      await invalidateProducts();
      await queryClient.invalidateQueries({ queryKey: ['products'] });
    } catch (err) {
      console.error('Erro ao deletar produto:', err);
      showApiErrorModal(modal, err, {
        title: "Erro ao deletar produto",
        fallbackMessage: "Erro ao deletar produto",
        autoClose: 3000,
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="pp-page">
      <header className="pp-header">
        <div>
          <h1 className="pp-title">Produtos</h1>
          <p className="pp-sub dashboard-page-subtitle">Crie, edite e gerencie produtos do coworking</p>
        </div>
        <div>
          <button className="pp-btn pp-btn-primary" onClick={() => setCreateOpen(true)}>+ Novo Produto</button>
        </div>
      </header>

      {/* Search and Filter Section */}
      <div className="pp-filters-section">
        <div className="pp-search-container">
          <div className="pp-search-input-wrapper">
            <Search className="pp-search-icon" size={16} />
            <input
              type="text"
              placeholder="Buscar por nome..."
              className="pp-search-input"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            />
          </div>
          <button 
            className={`pp-filter-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filtros {hasActiveFilters && <span className="pp-filter-badge">●</span>}
          </button>
          {hasActiveFilters && (
            <button className="pp-clear-filters" onClick={clearFilters}>
              <X size={14} /> Limpar
            </button>
          )}
        </div>

        {showFilters && (
          <div className="pp-filters-row">
            <div className="pp-filter-group">
              <label className="pp-filter-label">Tipo</label>
              <select 
                className="pp-filter-select"
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value as ProductType | ''); setPage(1); }}
              >
                <option value="">Todos</option>
                <option value="SERVICO">Serviço</option>
                <option value="ESPACO_COMODIDADE">Espaço/Comodidade</option>
                <option value="OUTRO">Outro</option>
              </select>
            </div>
            <div className="pp-filter-group">
              <label className="pp-filter-label">Recorrente</label>
              <select 
                className="pp-filter-select"
                value={recurringFilter}
                onChange={(e) => { setRecurringFilter(e.target.value as 'true' | 'false' | ''); setPage(1); }}
              >
                <option value="">Todos</option>
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </div>
          </div>
        )}
      </div>

      <section className="pp-card">
        <table className="pp-table">
          <thead>
            <tr>
              <th className="pp-sortable" onClick={() => handleSort('name')}>
                <span className="pp-th-content">
                  Nome
                  <SortIcon field="name" currentSort={sortBy} currentOrder={sortOrder} />
                </span>
              </th>
              <th className="pp-sortable" onClick={() => handleSort('type')}>
                <span className="pp-th-content">
                  Tipo
                  <SortIcon field="type" currentSort={sortBy} currentOrder={sortOrder} />
                </span>
              </th>
              <th>Descrição</th>
              <th className="pp-sortable" onClick={() => handleSort('price')}>
                <span className="pp-th-content">
                  Preço
                  <SortIcon field="price" currentSort={sortBy} currentOrder={sortOrder} />
                </span>
              </th>
              <th>Recorrente</th>
              <th style={{ textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody className="pp-table-body">
            {loading ? (
              <tr><td colSpan={6} className="pp-empty">Carregando...</td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={6} className="pp-empty">Nenhum produto encontrado.</td></tr>
            ) : (
              products.map((p) => (
                <tr key={p.id}>
                  <td className="pp-td-name">{p.name}</td>
                  <td className="pp-td-type">
                    <span className={`pp-type-badge pp-type-${p.type?.toLowerCase() || 'outro'}`}>
                      {ProductTypeLabels[p.type] || 'Outro'}
                    </span>
                  </td>
                  <td className="pp-td-desc">{p.description}</td>
                  <td className="pp-td-price">R$ {Number(p.price).toFixed(2)}</td>
                  <td className="pp-td-recurring">
                    <span className={`pp-recurring-badge ${p.isRecurring ? 'pp-recurring-yes' : 'pp-recurring-no'}`}>
                      {p.isRecurring ? 'Sim' : 'Não'}
                    </span>
                  </td>
                  <td className="pp-td-actions">
                    <button className="pp-action" onClick={() => { setEditing(p); setEditOpen(true); }}>Editar</button>
                    <button
                      className="pp-action"
                      onClick={() => { setDeletingId(p.id); setDeletingName(p.name); setConfirmOpen(true); }}
                    >
                      Apagar
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="pp-pagination">
          <div className="pp-total">{total ? `Total: ${total}` : ''}</div>
          <div className="pp-pages">
            <button className="pp-page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <div className="pp-page-indicator">{page}</div>
            <button className="pp-page-btn" onClick={() => setPage((p) => p + 1)}>›</button>
          </div>
        </div>
      </section>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)}>
        <h2 className="pp-modal-title">Novo Produto</h2>
        <ProductForm onCancel={() => setCreateOpen(false)} onSave={handleCreate} submitting={saving} />
      </Modal>

      <Modal open={editOpen} onClose={() => setEditOpen(false)}>
        <h2 className="pp-modal-title">Editar Produto</h2>
        <ProductForm initial={editing ?? undefined} onCancel={() => setEditOpen(false)} onSave={(payload) => editing && handleEdit(editing.id, payload)} submitting={saving} />
      </Modal>

      {/* Modal de confirmação de delete */}
      <ConfirmModal
        open={confirmOpen}
        title="Remover produto"
        message={deletingName ? `Deseja remover o produto "${deletingName}"? Esta ação não poderá ser desfeita.` : "Deseja remover este produto?"}
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}
